import { Input } from '@heroui/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FIELD_FOCUS_BORDER } from './fields.js';

/**
 * Search and filter patterns.
 *
 * One shape for "narrow this list down", so a technician who has learned it on
 * the users page already knows it on samples and reports.
 *
 * Honesty note: filters narrow what is SHOWN. An empty result after filtering
 * means nothing matched these filters — never that nothing exists — and the
 * empty state wording that goes with it says so.
 */

export interface SearchFieldProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Already translated. */
  readonly label?: string;
  readonly placeholder?: string;
  /**
   * Take the caret on mount.
   *
   * For the one case that earns it: a barcode scanner is a keyboard, and it
   * types wherever the caret already is. On a register whose primary use is
   * scanning, the field must be ready without a click. Off by default, because
   * stealing focus on an ordinary page is an accessibility problem rather than
   * a convenience.
   */
  readonly autoFocus?: boolean;
}

export function SearchField({
  value,
  onChange,
  label,
  placeholder,
  autoFocus = false,
}: SearchFieldProps) {
  const { t } = useTranslation();

  return (
    <Input
      // `type="search"` gives the browser's own clear affordance and tells
      // assistive technology what the field is for.
      type="search"
      // The same height as the select fields it sits beside in a filter row.
      size="md"
      variant="bordered"
      className="max-w-xs"
      // The same resting look, focus border and label position as every other
      // field (`fields.tsx`): with plain `outside` and no placeholder, the label
      // sat INSIDE the box and read as a placeholder (UX pass).
      classNames={FIELD_FOCUS_BORDER}
      label={label ?? t('common.search')}
      labelPlacement="outside-top"
      {...(placeholder ? { placeholder } : {})}
      value={value}
      onValueChange={onChange}
      autoFocus={autoFocus}
      isClearable
      onClear={() => onChange('')}
    />
  );
}

/**
 * The row of controls above a list.
 *
 * `flex-wrap` rather than a fixed column count: on a tablet the controls wrap
 * to a second line instead of shrinking to unusable widths, and on a phone they
 * stack. `gap` and `ms-auto` are logical, so the arrangement mirrors under RTL.
 */
export function FilterBar({
  children,
  actions,
}: {
  children: ReactNode;
  /** Pushed to the trailing edge — a reset control, typically. */
  actions?: ReactNode;
}) {
  /*
    Each control takes a compact fixed width from `sm` up, so a search and
    two selects sit on ONE row instead of every select stretching to the full
    width and stacking (D278 browser check). On a phone they still stack.
  */
  return (
    <div className="mb-stack flex flex-wrap items-end gap-x-stack gap-y-3 sm:[&>*:not([data-filter-actions])]:w-60 sm:[&>*:not([data-filter-actions])]:flex-none">
      {children}
      {actions ? (
        <div data-filter-actions="" className="ms-auto flex items-end gap-inline">
          {actions}
        </div>
      ) : null}
    </div>
  );
}
